import { memo, type ReactNode, useMemo } from 'react';
import { css, type Theme } from '@emotion/react';
import { WarningCircle } from '@phosphor-icons/react';
import { useEditAppValidation } from 'client/lib/EditApp';
import { Typography } from 'client/components/Typography';
import { useProjectOrDeploymentState } from 'client/sections/sectionsUtils';
import { EnvironmentInput } from './EnvironmentInput';

export interface EnvironmentProps {
  stackComponentID: number | null;
  label?: string | JSX.Element;
  path?: string;
  children?: ReactNode;
}

export const Environment = memo(
  ({ stackComponentID, label, path, children }: EnvironmentProps) => {
    const indexCount = useProjectOrDeploymentState(
      (state) =>
        (
          state.document?.componentsWithValues.find((component) => component.stackComponentID === stackComponentID)?.environmentVariables ||
          []
        ).length + 1
    );
    const indexArray = useMemo(() => new Array(indexCount).fill(null), [indexCount]);
    const validationError = useEditAppValidation(path || null);

    return (
      <div>
        {label && (
          <Typography component="label" css={labelStyle}>
            {label}
            {validationError && (
              <span>
                {' '}
                — <WarningCircle weight="bold" aria-hidden /> {validationError}
              </span>
            )}
          </Typography>
        )}
        <div css={listStyle}>
          {indexArray.map((_, index) => (
            <EnvironmentInput key={index} stackComponentID={stackComponentID} index={index} path={path ? `${path}.${index}` : undefined} />
          ))}
        </div>
        {/* <div css={actionsStyle}>
        <Button icon={<ArrowSquareIn />} small onClick={handleImport}>
          Import
        </Button>
        <Button icon={<ArrowSquareOut />} small onClick={handleExport}>
          Export
        </Button>
      </div> */}

        {children}
      </div>
    );
  },
  (prev, next) => {
    if (prev.stackComponentID !== next.stackComponentID) return false;
    if (prev.path !== next.path) return false;
    return true;
  }
);

const listStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const labelStyle = (theme: Theme) => css`
  display: flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  font-weight: 700 !important;
  font-size: 16px;
  transform-origin: 0 50%;
  transform: translateX(0) scale(0.75);

  > svg,
  > figure {
    transform: translateY(1px);
  }

  > span {
    color: ${theme.color.red[100]};
    opacity: 0.8;

    svg {
      transform: translateY(3px);
    }
  }
`;

const actionsStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;
